import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';
import { useTweenedNumbers } from '../../../hooks/useTweenedNumbers';
import {
  QUICK_PERIODS,
  QUICK_PERIOD_LABEL_KEYS,
  isQuickPeriod,
  type QuickPeriod,
} from '../../../utils/periodRange';

interface CategoryItem {
  label: string;
  sales: number;
  color: string;
}

interface TopCategoriesPanelProps {
  categories: CategoryItem[];
  totalCategories: number;
  totalProducts: number;
  isLoading?: boolean;
  className?: string;
  /**
   * Bug #32: kỳ đang chọn ở dropdown của panel. State do DashboardPage giữ vì
   * chính trang mới gọi /reports/top-products-by-category/ với khoảng ngày này.
   */
  period: QuickPeriod;
  onPeriodChange: (period: QuickPeriod) => void;
  /**
   * Đang tải dữ liệu của kỳ MỚI trong khi vẫn hiển thị số của kỳ cũ
   * (`isFetching && isPlaceholderData`). Chỉ dùng để làm mờ nhẹ phần thân,
   * KHÔNG dựng skeleton — skeleton chỉ dành cho lần tải đầu tiên.
   */
  isRefreshing?: boolean;
}

// Không có giá trị mặc định: mọi số hiển thị đều phải đến từ API.
export function TopCategoriesPanel({
  categories,
  totalCategories,
  totalProducts,
  isLoading = false,
  className,
  period,
  onPeriodChange,
  isRefreshing = false,
}: TopCategoriesPanelProps) {
  const { t } = useI18n();

  // Mờ nhẹ trong lúc tải nền. Không chặn tương tác, không spinner.
  const dimClass = cn('transition-opacity', isRefreshing && 'opacity-60');

  // Compute real percentages from sales, last slice gets remainder to ensure 100%
  const totalSales = categories.reduce((s, c) => s + c.sales, 0);
  const pcts = categories.map((c, i) => {
    if (i === categories.length - 1) {
      const sumSoFar = categories.slice(0, i).reduce((s, c2) => s + Math.round((c2.sales / totalSales) * 100), 0);
      return 100 - sumSoFar;
    }
    return Math.round((c.sales / totalSales) * 100);
  });

  // Nội suy chính các con số phần trăm: lần đầu chạy từ 0 lên, còn khi đổi kỳ
  // lúc biểu đồ ĐÃ có dữ liệu thì morph thẳng từ giá trị đang hiển thị sang giá
  // trị mới (không vẽ lại từ 0). Hook PHẢI đứng trước mọi return sớm bên dưới —
  // rules-of-hooks.
  const tweenedPcts = useTweenedNumbers(pcts);

  // Chỉ chạy ở lần tải ĐẦU TIÊN: từ lần đổi kỳ trở đi query dùng
  // `keepPreviousData` nên vẫn còn dữ liệu cũ, `isLoading` không bật lại.
  if (isLoading) {
    return <div className={cn('h-[420px] animate-pulse rounded-xl bg-slate-200', className)} />;
  }

  // Donut geometry — arc paths with natural gaps
  const size = 140;
  const cx = size / 2;
  const cy = size / 2;
  const r = 44;
  const strokeW = 22;

  function polarToCartesian(angle: number) {
    const rad = (angle - 90) * (Math.PI / 180);
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(startDeg: number, endDeg: number) {
    const s = polarToCartesian(startDeg);
    const e = polarToCartesian(endDeg);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return { d: `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`, s, e };
  }

  // Dựng slices từ mảng ĐÃ TWEEN: mỗi frame vẽ lại arc theo số hiện tại nên cả
  // góc bắt đầu (cộng dồn) lẫn độ rộng của từng lát đều chuyển động khớp nhau.
  let cumDeg = 0;
  const slices = categories.map((c, i) => {
    const pct = tweenedPcts[i] ?? 0;
    const deg = (pct / 100) * 360;
    const startDeg = cumDeg;
    const endDeg = cumDeg + deg;
    const midDeg = cumDeg + deg / 2;
    const midRad = (midDeg - 90) * (Math.PI / 180);
    const br = r + strokeW / 2 + 16;
    const arc = arcPath(startDeg, endDeg);
    cumDeg += deg;
    return {
      ...c,
      pct,
      full: deg >= 359.999, // slice chiếm trọn vòng -> vẽ circle thay vì arc
      path: arc.d,
      bubble: {
        x: cx + br * Math.cos(midRad),
        y: cy + br * Math.sin(midRad),
        text: `${Math.round(pct)}%`,
        // Lát đang ~0 (mới nở ra hoặc sắp biến mất) thì bỏ bong bóng: nếu không
        // sẽ có một đống nhãn "0%" chồng lên nhau ở đầu/cuối lúc chuyển kỳ.
        show: pct >= 0.5,
      },
    };
  });

  const pad = 28;

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F0F0F0] px-6 py-3">
        <h3 className="font-sans text-xl font-bold text-[#1A1A1A]">{t('topCategories')}</h3>
        <div className="relative">
          {/* Bug #32: <option> phải có `value` là mã cố định — xem ghi chú cùng
              lỗi ở AdvancedInsightsPanel. */}
          <select
            value={period}
            onChange={(e) => {
              if (isQuickPeriod(e.target.value)) onPeriodChange(e.target.value);
            }}
            className="h-10 appearance-none rounded-xl border border-[#E0E0E0] bg-white pl-4 pr-9 text-sm text-[#444] focus:outline-none"
          >
            {QUICK_PERIODS.map((option) => (
              <option key={option} value={option}>
                {t(QUICK_PERIOD_LABEL_KEYS[option])}
              </option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 5l4 4 4-4" stroke="#666" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Donut + legend */}
      <div className={cn('flex items-center justify-center gap-6 px-6 py-2', dimClass)}>
          {/* CHỈ biểu đồ chuyển động; legend, bảng thống kê, header và dropdown
              đều đứng yên. TUYỆT ĐỐI KHÔNG đặt `key={period}` ở đây: remount là
              biểu đồ bị dựng lại từ 0, đúng thứ cần tránh khi đổi kỳ lúc đã có
              dữ liệu. Chuyển động do useTweenedNumbers lo.
              KHÔNG `overflow-hidden`: viewBox đã chừa `pad` để chứa bong bóng %
              và bóng đổ của chúng, cắt đi là mất phần rìa. */}
          <div className="shrink-0">
            <svg
              width={size + pad * 2}
              height={size + pad * 2}
              viewBox={`${-pad} ${-pad} ${size + pad * 2} ${size + pad * 2}`}
            >
              <defs>
                <filter id="bshadow" x="-60%" y="-60%" width="220%" height="220%">
                  <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#00000018" />
                </filter>
              </defs>
              {/* Bug #32: kỳ đang chọn không có đơn -> vẽ vòng rỗng thay vì một
                  khối trắng trông như lỗi render. Vòng này là NỀN, không phải
                  dữ liệu -> không animate. */}
              {categories.length === 0 && (
                <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={strokeW} />
              )}
              {/* slices — `d` được tính lại mỗi frame từ phần trăm đã nội suy,
                  không còn stroke-dash/animation của CSS. */}
              {slices.map((s, i) =>
                s.full ? (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={strokeW}
                  />
                ) : (
                  <path
                    key={i}
                    d={s.path}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={strokeW}
                  />
                )
              )}
              {/* bubble labels — vị trí và số đều bám theo giá trị đã tween */}
              {slices.map((s, i) =>
                s.bubble.show ? (
                  <g key={i}>
                    <circle cx={s.bubble.x} cy={s.bubble.y} r={18} fill="white" filter="url(#bshadow)" />
                    <text
                      x={s.bubble.x} y={s.bubble.y + 4}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="700"
                      fill={s.color}
                      fontFamily="Inter, system-ui, sans-serif"
                    >
                      {s.bubble.text}
                    </text>
                  </g>
                ) : null
              )}
            </svg>
          </div>

          {/* Legend: KHÔNG tween — tên danh mục và số sales đổi thẳng sang dữ
              liệu mới, chỉ vòng donut mới chuyển động. */}
          <div className="flex flex-col gap-2">
            {categories.length === 0 && (
              <span className="text-sm text-[#9E9E9E]">{t('noDataInPeriod')}</span>
            )}
            {categories.map((c, i) => (
              <div key={i}>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-1.5 rounded-full" style={{ backgroundColor: c.color }} />
                  <span className="text-xs text-[#9E9E9E]">{c.label}</span>
                </div>
                <p className="mt-0.5 text-base font-bold text-[#1A1A1A]">
                  {c.sales}{' '}
                  <span className="font-semibold">{t('sales')}</span>
                </p>
              </div>
            ))}
          </div>
      </div>

      <div className={cn('px-6 pb-3', dimClass)}>
        <p className="mb-2 text-sm font-bold text-[#1A1A1A]">{t('categoryStatistics')}</p>
        <div className="overflow-hidden rounded-xl border border-[#F0F0F0]">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#3538CD]" />
              <span className="text-sm text-[#9E9E9E]">{t('totalNumberOfCategories')}</span>
            </div>
            <span className="text-sm font-bold text-[#1A1A1A]">{totalCategories}</span>
          </div>
          <div className="flex items-center justify-between border-t border-[#F0F0F0] px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#E04F16]" />
              <span className="text-sm text-[#9E9E9E]">{t('totalNumber1ofProducts')}</span>
            </div>
            <span className="text-sm font-bold text-[#1A1A1A]">{totalProducts.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
